<template>
  <Transition name="StaffSelect">
    <div
      v-modal-focus
      v-if="isModalStaffSelectVisible"
      :id="staffSelectId"
      class="modal-mask"
    >
      <MStaffSearch
        :isModalStaffSearchVisible="isModalStaffSearchVisible"
        :StaffSearchId="StaffSearchId"
        @selected-action="selectedActionStaffSearch"
        @cancel-action="cancelActionStaffSearch"
      />

      <div class="modal-container">
        <div class="modal-header">
          <div
            style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              width: 500px;
            "
          >
            <h6 class="modal-header-title">スタッフ選択</h6>
          </div>
        </div>
        <!-- エラー情報 -->
        <div v-if="Errors && Errors.length > 0">
          <table class="alert alert-danger table-sm">
            <tr v-for="(error, index) in Errors" :key="index">
              <td>{{ error }}</td>
            </tr>
          </table>
        </div>
        <div class="modal-body">
          <div class="table-responsive">
            <div class="card">
              <div class="card-body">
                <div class="row mb-1">
                  <div class="col-sm-12">
                    <div class="input-group">
                      <span class="input-group-text">スタッフ</span>
                      <input type="text" class="form-control" v-model="Staff.staffCode" />

                      <ComboBox
                        v-model="Staff.staffCode"
                        :comboData="Staffs"
                        keyField="staffCode"
                        valueField="staffName"
                        :isEmpty="true"
                        :emptyCode="'0'"
                        :emptyName="''"
                        :class-name="'form-control'"
                        style="width: 100px"
                      />
                      <button
                        class="btn btn-primary mx-0"
                        style="width: 40px"
                        @click="openStaffSearch"
                      >
                        ▽
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer d-flex justify-content-end">
            <button class="btn btn-primary m-2" @click="selectedAction(Staff)">
              選択
            </button>
            <button class="btn btn-dark m-3 btn-other" @click="cancelAction">
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, inject } from "vue";
import type { Ref } from "vue";
import axios from "axios";
import { TblMStaff } from "@/models/TblMStaff";
import type { ITblMStaffData } from "@/models/TblMStaff";
import MyCommon from "@/Js/MyCommon";
import MStaffSearch from "@/components/MStaffSearch.vue";
import { useStaffSearch } from "@/models/TblMStaff";
import StaffSelectService from "@/Js/StaffSelectService";
import ComboBox from "@/components/MyComboBox.vue";
import type { ToastPluginApi } from "vue-toast-notification";

const toast: ToastPluginApi | undefined = inject("toast");
let Errors: Ref<string[]> = ref([]);
let Staffs: TblMStaff[] = [new TblMStaff()];
let Staff: Ref<TblMStaff> = ref(new TblMStaff());

const myCommon = new MyCommon();
const staffSelectService = new StaffSelectService();
const init = async () => {
  Errors.value = [];
  await getStaff();
  Staff.value.staffCode = staffSelectService.getStaffSelect().staffCode ?? "";
};

const {
  StaffSearchId,
  isModalStaffSearchVisible,
  showStaffSearch,
  closeModalStaffSearch,
  cancelActionStaffSearch,
  selectedActionStaffSearch,
} = useStaffSearch();

const openStaffSearch = () => {
  showStaffSearch((staff: TblMStaff) => {
    Staff.value.staffCode = staff.staffCode;
  });
};

// Props と Emits
//「コンポーネント間のデータのやり取り」では
//「親から子」にdataを伝達する手段「props」
//「子から親」にdataを伝達する手段「emit」が存在します。

//親から子」にdataを伝達する手段「props」親から設定する。
const props = defineProps({
  staffSelectId: String,
  isModalStaffSelectVisible: Boolean,
  selectedActionStaffSelectCallback: Function,
});

const emit = defineEmits(["cancel-action", "close-modal", "selected-action"]);

//const { isModalVisible } = toRefs(props);

const closeModal = () => {
  emit("close-modal");
};

const cancelAction = () => {
  Errors.value = [];
  if (myCommon.isNullOrEmpty(staffSelectService.getStaffSelect())) {
    Errors.value.push("スタッフを選択してください。");
    return;
  }

  emit("cancel-action");
  closeModal();
};

// ユーザー選択時の処理
const selectedAction = (staff: TblMStaff) => {
  Errors.value = [];
  if (myCommon.isNullOrEmpty(staff.staffCode)) {
    Errors.value.push("スタッフを選択してください。");
    return;
  }

  if (!Staffs.some((item: TblMStaff) => item.staffCode == staff.staffCode)) {
    Errors.value.push("スタッフを正しく選択してください。");
    return;
  }

  staffSelectService.setStaffSelect(
    staff.staffCode,
    Staffs.find((item: TblMStaff) => item.staffCode == staff.staffCode)?.staffName
  );

  emit("selected-action", staff);
  if (props.selectedActionStaffSelectCallback) {
    props.selectedActionStaffSelectCallback(staff);
  }
  closeModal();
};

async function getStaff() {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_TARGET}/api/mStaff/GetLists`
    );
    if (response.status === 200) {
      const StaffList: Ref<TblMStaff[]> = ref([new TblMStaff()]);
      StaffList.value = response.data
        .map((item: ITblMStaffData) => {
          const jstbirthday = myCommon.convertUtcToLocal(item.birthday);

          // 新しいユーザーオブジェクトを作成し、変換された日付を使用
          return new TblMStaff({
            ...item,
            birthday: jstbirthday,
          });
        })
        .sort((a: ITblMStaffData, b: ITblMStaffData) => {
          // StaffCodeを数値に変換して比較
          return Number(a.staffCode) - Number(b.staffCode);
        });

      Staffs = StaffList.value;
    }
  } catch (error: any) {
    // サーバーからのエラーメッセージを取得
    if (error.response) {
      const errorMessage = error.response.data ?? "エラーが発生し処理を中止しました。";
      console.error("API Error:", errorMessage);
      toast?.error(errorMessage);
      throw new Error(errorMessage); // エラーをスローして呼び出し元に渡す
    } else {
      console.error("Unexpected Error:", error);
      toast?.error("通信エラーが発生しました。");
      throw new Error("通信エラーが発生しました。");
    }
  }
}

// 初期化処理
onMounted(async () => {
  await init();
});

watch(
  () => props.isModalStaffSelectVisible,
  async (newValue, oldValue) => {
    if (newValue !== oldValue && newValue == true) {
    }
  }
);
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  right: 0; /* 画面全体に広がるように調整 */
  bottom: 0; /* 画面全体に広がるように調整 */
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center; /* 中央に配置 */
  justify-content: center; /* 中央に配置 */
  transition: opacity 0.3s ease;
}

.modal-container {
  width: 600px; /* モーダルの幅 */

  padding: 20px 30px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
}

.modal-header,
.modal-footer {
  padding: 2px 0;
  border-bottom: 1px solid #dee2e6;
}

.modal-header:last-child {
  border-bottom: none;
}

.modal-header-title {
  color: black;
}
.modal-body {
  margin: 5px 0;
  overflow-y: auto; /* 内容が多い場合にスクロール可能に */
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.5s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(1.1);
}
</style>
